import { useEffect, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { Keyboard, KeyboardResize } from '@capacitor/keyboard';

/**
 * Keep a chat composer glued to the software keyboard.
 *
 * iOS Capacitor's default Native resize shrinks the WKWebView *after* the
 * keyboard has already animated up, so the input jumps a beat later. While a
 * conversation is open we turn that resize off and apply the keyboard height
 * ourselves on `keyboardWillShow` (the start of the system animation).
 */
export function useKeyboardInset(enabled: boolean): number {
  const [inset, setInset] = useState(0);

  useEffect(() => {
    if (!enabled) {
      setInset(0);
      document.body.classList.remove('keyboard-open');
      document.documentElement.style.removeProperty('--keyboard-inset');
      return;
    }

    let cancelled = false;
    const handles: Array<{ remove: () => Promise<void> }> = [];
    const isNative = Capacitor.isNativePlatform();
    const isNativeIOS = isNative && Capacitor.getPlatform() === 'ios';

    const apply = (next: number) => {
      if (cancelled) return;
      const value = Math.max(0, Math.round(next));
      setInset(value);
      document.documentElement.style.setProperty('--keyboard-inset', `${value}px`);
      document.body.classList.toggle('keyboard-open', value > 0);
    };

    if (isNativeIOS) {
      void Keyboard.setResizeMode({ mode: KeyboardResize.None });
    }

    if (isNativeIOS) {
      void Keyboard.addListener('keyboardWillShow', (info) => {
        apply(info.keyboardHeight);
      }).then((handle) => {
        if (cancelled) {
          void handle.remove();
          return;
        }
        handles.push(handle);
      });
      void Keyboard.addListener('keyboardWillHide', () => {
        apply(0);
      }).then((handle) => {
        if (cancelled) {
          void handle.remove();
          return;
        }
        handles.push(handle);
      });
    }

    const viewport = window.visualViewport;
    const syncViewport = () => {
      if (!viewport || isNative) return;
      const next = Math.max(0, window.innerHeight - viewport.height - viewport.offsetTop);
      apply(next > 80 ? next : 0);
    };
    viewport?.addEventListener('resize', syncViewport);
    viewport?.addEventListener('scroll', syncViewport);

    return () => {
      cancelled = true;
      handles.forEach((handle) => {
        void handle.remove();
      });
      viewport?.removeEventListener('resize', syncViewport);
      viewport?.removeEventListener('scroll', syncViewport);
      document.body.classList.remove('keyboard-open');
      document.documentElement.style.removeProperty('--keyboard-inset');
      setInset(0);
      if (isNativeIOS) {
        void Keyboard.setResizeMode({ mode: KeyboardResize.Native });
      }
    };
  }, [enabled]);

  return inset;
}
